import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

// Close-grace delay prevents flicker when the cursor travels from the primary
// item to the panel through a few pixels of gap.
const CLOSE_GRACE_MS = 80;

const MegaMenu = ({ categoryNav }) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);
  const [underline, setUnderline] = useState({ left: 0, width: 0, visible: false });

  const closeTimerRef = useRef(null);
  const primaryRef = useRef(null);
  const itemRefs = useRef([]);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const closeMenu = useCallback(() => {
    clearCloseTimer();
    setActiveIndex(null);
    setUnderline((prev) => ({ ...prev, visible: false }));
  }, []);

  const openForIndex = useCallback((index) => {
    clearCloseTimer();
    setActiveIndex(index);
    setActiveCategoryIndex(0); // Reset persona tab when switching top-level items.

    const el = itemRefs.current[index];
    const primary = primaryRef.current;

    if (!el || !primary) return;

    const rect = el.getBoundingClientRect();
    const primaryRect = primary.getBoundingClientRect();

    setUnderline({
      left: rect.left - primaryRect.left,
      width: rect.width,
      visible: true,
    });
  }, []);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      closeMenu();
    }, CLOSE_GRACE_MS);
  }, [closeMenu]);

  const cancelClose = useCallback(() => {
    clearCloseTimer();
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape" && activeIndex !== null) {
        closeMenu();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [activeIndex, closeMenu]);

  useEffect(() => () => clearCloseTimer(), []);

  if (!Array.isArray(categoryNav) || categoryNav.length === 0) {
    return null;
  }

  const activeTopLevel = activeIndex !== null ? categoryNav[activeIndex] : null;
  const categories = activeTopLevel ? activeTopLevel.categories : [];
  const activeCategory = categories[activeCategoryIndex] || categories[0];
  const groups = activeCategory ? activeCategory.groups : [];

  return (
    <div
      className="mega-menu"
      onMouseLeave={scheduleClose}
      onMouseEnter={cancelClose}
    >
      <div className="mega-menu__primary" ref={primaryRef}>
        {categoryNav.map((item, i) => (
          <button
            key={item.label}
            ref={(el) => { itemRefs.current[i] = el; }}
            className="mega-menu__primary-item"
            type="button"
            onMouseEnter={() => openForIndex(i)}
            onFocus={() => openForIndex(i)}
          >
            {item.label}
          </button>
        ))}
        <div
          className={`mega-menu__underline${underline.visible ? " mega-menu__underline--visible" : ""}`}
          style={{
            left: `${underline.left}px`,
            width: `${underline.width}px`,
          }}
        />
      </div>

      <div className={`mega-menu-clip${activeIndex !== null ? " mega-menu-clip--open" : ""}`}>
        <div className={`mega-menu-panel${activeIndex !== null ? " mega-menu-panel--open" : ""}`}>
          <div className="mega-menu-panel__container">
            <div className="mega-menu-panel__category-nav">
              {categories.map((cat, i) => (
                <button
                  key={cat.label}
                  type="button"
                  className={`mega-menu-panel__category-item${i === activeCategoryIndex ? " mega-menu-panel__category-item--active" : ""}`}
                  onClick={() => setActiveCategoryIndex(i)}
                >
                  {cat.label}
                </button>
              ))}
            </div>
            <div className="mega-menu-panel__grid">
              {groups.map((group) => (
                <div key={group.heading} className="mega-menu-panel__group">
                  {group.view_all_href ? (
                    <a
                      href={group.view_all_href}
                      className="mega-menu-panel__group-heading mega-menu-panel__group-heading--link"
                    >
                      {group.heading}
                    </a>
                  ) : (
                    <p className="mega-menu-panel__group-heading">{group.heading}</p>
                  )}
                  {group.links.map((link) => (
                    <a
                      key={link.label}
                      href={link.href}
                      className="mega-menu-panel__group-link"
                    >
                      {link.label}
                    </a>
                  ))}
                  {group.view_all_href && (
                    <a
                      href={group.view_all_href}
                      className="mega-menu-panel__group-link mega-menu-panel__group-link--view-all"
                    >
                      <span>View all</span>
                      <span aria-hidden="true"> &rarr;</span>
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

MegaMenu.propTypes = {
  categoryNav: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      categories: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string, // null allowed when no persona filter
          groups: PropTypes.arrayOf(
            PropTypes.shape({
              heading: PropTypes.string.isRequired,
              view_all_href: PropTypes.string,
              links: PropTypes.arrayOf(
                PropTypes.shape({
                  label: PropTypes.string.isRequired,
                  href: PropTypes.string.isRequired,
                })
              ).isRequired,
            })
          ).isRequired,
        })
      ).isRequired,
    })
  ),
};

export default MegaMenu;
